import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Download, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmtSize = (n) =>
  n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

export const Attachments = ({ referral }) => {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () =>
    api.get(`/referrals/${referral.id}/attachments`).then((r) => setFiles(r.data)).catch(() => {});

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral.id]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds the 25 MB limit");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/referrals/${referral.id}/attachments`, fd);
      toast.success(`${file.name} attached`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const download = async (f) => {
    try {
      const r = await api.get(`/referrals/${referral.id}/attachments/${f.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const remove = async (f) => {
    try {
      await api.delete(`/referrals/${referral.id}/attachments/${f.id}`);
      toast.success(`${f.original_filename} removed`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4" data-testid={`attachments-${referral.id}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-navy/40">
          Scans & Reports ({files.length})
        </p>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`attach-button-${referral.id}`}
          className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-navy border border-navy/20 px-3 py-2 hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
        >
          <Paperclip size={12} /> {uploading ? "Uploading…" : "Attach File"}
        </button>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept=".pdf,.jpg,.jpeg,.png,.webp,.txt,.csv,.dcm"
          onChange={upload}
          data-testid={`attach-input-${referral.id}`}
        />
      </div>

      {files.length > 0 && (
        <ul className="space-y-2">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between gap-3 bg-ivory border border-navy/10 px-4 py-2.5"
              data-testid={`attachment-${f.id}`}
            >
              <div className="min-w-0">
                <p className="text-xs text-navy truncate">{f.original_filename}</p>
                <p className="font-mono text-[9px] text-navy/40 mt-0.5">
                  {fmtSize(f.size)} · {f.uploaded_by_name} ·{" "}
                  {new Date(f.created_at).toLocaleDateString("en-GB")}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => download(f)}
                  data-testid={`download-attachment-${f.id}`}
                  className="p-2 text-navy/50 hover:text-gold transition-colors"
                  title="Download"
                >
                  <Download size={14} />
                </button>
                {(f.uploaded_by === user?.id || user?.role === "admin") && (
                  <button
                    onClick={() => remove(f)}
                    data-testid={`delete-attachment-${f.id}`}
                    className="p-2 text-navy/50 hover:text-crimson transition-colors"
                    title="Remove"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
